import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Loader2, AlertTriangle, CheckCircle2, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const SEVERITY_STYLES = {
  critical: "bg-red-500/10 text-red-400 border-red-500/20",
  high: "bg-orange-500/10 text-orange-400 border-orange-500/20",
  medium: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  low: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
};

const PRIORITY_STYLES = {
  P0: "bg-red-500/10 text-red-400 border-red-500/20",
  P1: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  P2: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
};

const SAMPLE_INPUTS = [
  { type: "api", value: "https://api.example.com/v1/users", label: "REST API Endpoint" },
  { type: "url", value: "https://app.example.com/dashboard", label: "Web Application" },
  { type: "api", value: "https://api.example.com/v1/auth/login", label: "Auth Endpoint" },
];

export const DemoPlayground = ({ apiUrl }) => {
  const [selectedInput, setSelectedInput] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleRun = async () => {
    setLoading(true);
    setResult(null);
    try {
      const input = SAMPLE_INPUTS[selectedInput];
      const res = await axios.post(`${apiUrl}/playground/run`, {
        input_type: input.type,
        input_value: input.value,
      });
      setResult(res.data);
    } catch (err) {
      console.error("Playground run failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="playground"
      data-testid="playground-section"
      className="relative py-24 lg:py-32"
    >
      {/* Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-arena-blue/[0.04] rounded-full blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <span className="text-xs font-mono text-arena-blue uppercase tracking-[0.2em]">
            Interactive
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mt-3 tracking-tight">
            Demo Playground
          </h2>
          <p className="mt-4 text-sm font-mono text-zinc-500 max-w-lg">
            Try a QA Agent. Select a target and watch it detect bugs and generate test cases.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          {/* Input Panel */}
          <div className="bg-arena-surface border border-white/[0.06] rounded-md p-6">
            <div className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-4">
              Select Target
            </div>

            <div className="space-y-2 mb-6">
              {SAMPLE_INPUTS.map((input, idx) => (
                <button
                  key={idx}
                  data-testid={`playground-input-${idx}`}
                  onClick={() => {
                    setSelectedInput(idx);
                    setResult(null);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-md border transition-colors font-mono text-sm ${
                    selectedInput === idx
                      ? "border-arena-blue/40 bg-arena-blue/[0.06] text-white"
                      : "border-white/[0.06] bg-transparent text-zinc-500 hover:border-white/[0.1] hover:text-zinc-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{input.label}</span>
                    <ChevronRight className="w-3 h-3" />
                  </div>
                  <div className="text-xs text-zinc-600 mt-1 truncate">
                    {input.value}
                  </div>
                </button>
              ))}
            </div>

            <Button
              data-testid="playground-run-btn"
              onClick={handleRun}
              disabled={loading}
              className="w-full bg-white text-black hover:bg-zinc-200 font-mono text-sm h-11 rounded-md gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Running Agent...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Run QA Agent
                </>
              )}
            </Button>
          </div>

          {/* Output Panel */}
          <div className="bg-arena-surface border border-white/[0.06] rounded-md p-6 min-h-[320px]">
            <div className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-4">
              Output
            </div>

            <AnimatePresence mode="wait">
              {!result && !loading && (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center justify-center h-60"
                >
                  <p className="text-sm font-mono text-zinc-600">
                    Select a target and run the agent to see results.
                  </p>
                </motion.div>
              )}

              {loading && (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center h-60 gap-3"
                >
                  <Loader2 className="w-6 h-6 text-arena-blue animate-spin" />
                  <p className="text-sm font-mono text-zinc-500">
                    Analyzing target...
                  </p>
                </motion.div>
              )}

              {result && !loading && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="space-y-4"
                >
                  {/* Summary */}
                  <div className="p-3 bg-arena-elevated border border-white/[0.06] rounded-md">
                    <p className="text-xs font-mono text-zinc-400">
                      {result.summary}
                    </p>
                    <p className="text-xs font-mono text-zinc-600 mt-1">
                      Execution time: {result.execution_time}
                    </p>
                  </div>

                  {/* Bugs */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                      <span className="text-xs font-mono text-zinc-400 uppercase tracking-wider">
                        Bugs Found ({result.bugs_found.length})
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {result.bugs_found.map((bug) => (
                        <div
                          key={bug.id}
                          data-testid={`bug-${bug.id}`}
                          className="flex items-center justify-between p-2.5 bg-arena-elevated border border-white/[0.04] rounded-md"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <Badge
                              variant="outline"
                              className={`font-mono text-[10px] shrink-0 ${
                                SEVERITY_STYLES[bug.severity]
                              }`}
                            >
                              {bug.severity}
                            </Badge>
                            <span className="text-xs font-mono text-zinc-300 truncate">
                              {bug.title}
                            </span>
                          </div>
                          <span className="text-[10px] font-mono text-zinc-600 shrink-0 ml-2">
                            {(bug.confidence * 100).toFixed(0)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Test Cases */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="text-xs font-mono text-zinc-400 uppercase tracking-wider">
                        Test Cases ({result.test_cases.length})
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {result.test_cases.map((tc) => (
                        <div
                          key={tc.id}
                          data-testid={`test-case-${tc.id}`}
                          className="flex items-center justify-between p-2.5 bg-arena-elevated border border-white/[0.04] rounded-md"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <Badge
                              variant="outline"
                              className={`font-mono text-[10px] shrink-0 ${
                                PRIORITY_STYLES[tc.priority]
                              }`}
                            >
                              {tc.priority}
                            </Badge>
                            <span className="text-xs font-mono text-zinc-300 truncate">
                              {tc.name}
                            </span>
                          </div>
                          <span className="text-[10px] font-mono text-zinc-600 shrink-0 ml-2">
                            {tc.type}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
